/* eslint-disable react/no-array-index-key */
import { Button, Link, NotificationInline, OverlayLoader } from '@coop/components';
import { useEffect } from 'react';

import RecipeClimateImpact from '../../../../recipe/components/RecipeClimateImpact/RecipeClimateImpact';
import { useRecipeByExternalIdQuery } from '../../../../recipe/hooks/useRecipeByExternalIdQuery';
import { buildRecipeRedirectUrl } from '../../../../recipe/utils';
import CloudinaryImage from '../../atoms/CloudinaryImage';
import FavoriteRecipeToggle from '../../molecules/FavoriteRecipeToggle';
import { RatingStars } from '../../molecules/RatingStars';
import { useBuyableRecipeFlyInTracking } from '../BuyableRecipeFlyIn.hooks';
import styles from './BuyableRecipeDetails.module.scss';
import BuyableRecipeDetailsMaybeTabs from './BuyableRecipeDetailsMaybeTabs';

const RecipeAttributes = (props: {
    cookingTime?: string;
    ovenTemperature?: string;
    portions?: number;
    portionsUnit?: string;
}) => {
    return (
        <div className={styles.Attributes}>
            {props.cookingTime && (
                <p className={styles.Attribute}>
                    Tillagningstid: <span className={styles.Bold}>{props.cookingTime}</span>
                </p>
            )}
            {props.ovenTemperature && (
                <p className={styles.Attribute}>
                    Ugnstemperatur: <span className={styles.Bold}>{props.ovenTemperature}</span>
                </p>
            )}
            {props.portions && props.portionsUnit && (
                <p className={styles.Attribute}>
                    Portioner: <span className={styles.Bold}>{`${props.portions}st`}</span>
                </p>
            )}
        </div>
    );
};

const BuyableRecipeDetails = (props: {
    allowRecipePageRedirect: boolean;
    recipeId: string;
    recipeType: 'recipe' | 'foodbox';
    onBuyIntentClick?: () => void;
    navigationSlot?: React.ReactNode;
}) => {
    const { trackModalPopup, trackLinkClick } = useBuyableRecipeFlyInTracking();

    const {
        data: recipe,
        error: recipeError,
        isLoading: isRecipeLoading,
    } = useRecipeByExternalIdQuery(props.recipeId);

    useEffect(() => {
        if (recipe) {
            trackModalPopup(recipe, recipe.yieldValue);
        }
    }, [recipe, trackModalPopup]);

    const handleLinkClick = () => {
        if (recipe) {
            trackLinkClick(recipe.name, props.recipeId, recipe.yieldValue);
        }
    };

    return (
        <div className={styles.FullHeight}>
            <div className={styles.Container}>
                <OverlayLoader isLoading={isRecipeLoading} />
                {props.navigationSlot}
                <NotificationInline
                    variant="critical"
                    style={{ margin: 'var(--cds-spacing-md)' }}
                    role="alert"
                    animated={false}
                    show={!!recipeError}
                    message="Vi kunde inte ladda receptet"
                />
                {recipe && (
                    <>
                        <CloudinaryImage
                            cssClass={styles.Image}
                            url={recipe.imageUrl}
                            name={recipe.name}
                            fallbackUrl="/Assets/Images/missing-image-product.png"
                            transform="fl_progressive,q_auto,f_auto,c_fill,g_center,h_540,w_540"
                            retinaTransform="fl_progressive,q_auto,f_auto,c_fill,g_center,h_1080,w_1080"
                            lazy={false}
                        />
                        <div className={styles.Content}>
                            <div className={styles.Description}>
                                <h2 className={styles.RecipeName}>{recipe.name}</h2>
                                <div className={styles.Actions}>
                                    <RatingStars
                                        rating={recipe.avgRating}
                                        votes={recipe.numberOfVotes}
                                        color="greenDark"
                                        size="big"
                                    />
                                    <FavoriteRecipeToggle
                                        recipeId={recipe.externalId.toString()}
                                        recipeName={recipe.name}
                                        type={recipe.isFoodBox ? 'foodbox' : 'recipe'}
                                    />
                                </div>
                                <RecipeClimateImpact climateImpactKg={recipe.climateImpactKg} />
                                {recipe.preamble && <span>{recipe.preamble}</span>}
                                <RecipeAttributes
                                    cookingTime={recipe.cookingTime}
                                    ovenTemperature={recipe.ovenTemperature}
                                    portions={recipe.yieldValue}
                                    portionsUnit={recipe.yieldUnit}
                                />
                            </div>
                            {props.onBuyIntentClick && (
                                <Button theme="primary" fullWidth onClick={props.onBuyIntentClick}>
                                    Se och köp varor
                                </Button>
                            )}
                            <BuyableRecipeDetailsMaybeTabs recipe={recipe} />
                        </div>
                        <div className={styles.FillSpace} />
                        {props.allowRecipePageRedirect && (
                            <div className={styles.LinkContainer}>
                                <Link
                                    size="large"
                                    href={buildRecipeRedirectUrl(props.recipeId)}
                                    onClick={handleLinkClick}
                                >
                                    {props.recipeType === 'foodbox'
                                        ? 'Gå till produktsida'
                                        : 'Gå till receptsidan'}
                                </Link>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BuyableRecipeDetails;
